import { motion, useReducedMotion } from 'framer-motion';
import { useMemo } from 'react';

interface Sparkle {
  id: number;
  x: number;
  y: number;
  size: number;
  delay: number;
  duration: number;
  color: 'purple' | 'white';
}

interface SparkleBackgroundProps {
  count?: number;
  className?: string;
}

export function SparkleBackground({ count = 18, className }: SparkleBackgroundProps) {
  const prefersReducedMotion = useReducedMotion();

  const sparkles = useMemo<Sparkle[]>(() => {
    return Array.from({ length: count }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: 2 + Math.random() * 2, // 2-4px
      delay: Math.random() * 3, // 0-3s stagger
      duration: 3 + Math.random() * 3, // 3-6s per cycle
      color: Math.random() > 0.3 ? 'purple' : 'white',
    }));
  }, [count]);

  return (
    <div 
      className={`absolute inset-0 overflow-hidden pointer-events-none ${className ?? ''}`}
      style={{ zIndex: 5 }}
      aria-hidden="true"
    >
      {sparkles.map((sparkle) => (
        <motion.div
          key={sparkle.id}
          className="absolute rounded-full"
          style={{
            left: `${sparkle.x}%`,
            top: `${sparkle.y}%`,
            width: sparkle.size,
            height: sparkle.size,
            backgroundColor: sparkle.color === 'purple' 
              ? 'hsl(270 70% 60%)' 
              : 'hsl(0 0% 100%)',
            willChange: prefersReducedMotion ? 'auto' : 'opacity, transform',
          }}
          initial={{ opacity: 0.1, scale: 0.8 }}
          animate={
            prefersReducedMotion
              ? { opacity: 0.2 }
              : {
                  opacity: [0.1, 0.35, 0.1],
                  scale: [0.8, 1.2, 0.8],
                }
          }
          transition={
            prefersReducedMotion
              ? { duration: 0 }
              : {
                  duration: sparkle.duration,
                  delay: sparkle.delay,
                  repeat: Infinity,
                  ease: 'easeInOut',
                }
          }
        />
      ))}
    </div>
  );
}
